import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { API } from '../api';

const OpportunitiesColumns = ({ setLoading, setFeedback }) => {
    const handleOpen = async (qid, quoteNumber) => {
        setLoading(true);
        let url;
        let link;
        try {
            const res = await API.Quotes.PreviewPDF(qid);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            url = window.URL.createObjectURL(blob);
            // Match the quote page's mobile-compatible download. Opening a new
            // window after awaiting the API loses the browser's user gesture.
            link = document.createElement('a');
            link.href = url;
            link.download = `${quoteNumber || `quote-${qid}`}.pdf`;
            document.body.appendChild(link);
            link.click();
        } catch {
            setFeedback({ open: true, type: 'error', message: 'Unable to open the quote PDF. Please try again.' });
        } finally {
            link?.remove();
            // Give mobile viewers time to consume the blob before releasing it.
            if (url) setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            setLoading(false);
        }
    };

    // Define the breakpoints
    const isMobile = useMediaQuery('(max-width:600px)');

    return [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            renderCell: ({row: { _id, name } }) => <a className="hive-grid-link-cell" href={`/opportunities/${_id}`}>{name}</a>
        },
        // Conditionally show/hide columns
        ...(isMobile ? [] : [
            {
                field: "projectNumber",
                headerName: "Project #",
                flex: 1,
                renderCell: ({ value }) => {
                    return (
                    <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                    }}
                    >
                    <Typography sx={{ color: '#592e7dff', fontWeight: 500, fontSize: '0.875rem' }}>
                        {value || ""}
                    </Typography>
                    </Box>                        
                    )                    
                }
            },
            {
                field: "quoteNumber",
                headerName: "Quote #",
                flex: 1,
                renderCell: ({ row: { quoteNumber, quoteId } }) =>
                    quoteId ? (
                        <a className="hive-grid-link-cell" href={`/base-unique-quote/${quoteId}`}>
                            {quoteNumber}
                        </a>
                    ) : (
                        quoteNumber || ""
                    ),
            },
            {
                field: "customer",
                headerName: "Customer",
                flex: 1,
                renderCell: ({row: { customer } }) => <a className="hive-grid-link-cell" href={`/customers/${customer?._id}`}>{customer?.organizationName}</a>,
                // renderCell: ({row: { customer} }) => customer?.organizationName,
                valueGetter: (params) => params?.organizationName 
            },
            {
                field: "primaryContact",
                headerName: "Primary Contact",
                flex: 1,
                renderCell: ({row: { primaryContact} }) => primaryContact?.name,
                valueGetter: (params) => params?.name 
            },
            {
                field: "owner",
                headerName: "Owner",
                flex: 1,
                renderCell: ({ row: { owner } }) => owner ? `${owner.firstName} ${owner.lastName}` : 'Unassigned',
                valueGetter: (params) => `${params.firstName} ${params.lastName}` 
            },
            {
                field: "amount",
                headerName: "Amount",
                flex: 1,
                renderCell: ({ row: { amount }}) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '0.875rem' }}>
                            {amount ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                            }).format(amount) : '$0.00'}
                        </Typography>
                    </Box>
                )
            },
            {
                field: "closeDate",
                headerName: "Close Date",
                renderCell: ({ row: { closeDate } }) => closeDate ? closeDate.split("T")[0] : "",
                flex: 1
            }
        ]),
        {
            field: "salesStage",
            headerName: "Stage",
            flex: 1,
            renderCell: ({ row: { salesStage } }) => {
                return (
                    <label className={`badge  ${salesStage === '01 - Lead' ? 'bg-light-primary' : salesStage === '08 - Won' ? 'bg-light-success' :salesStage === '05 - Committee Approval' ? 'bg-light-warning' : salesStage === '04 - Internal Revision' ? 'bg-light-danger' : salesStage === '06 - Quote Sent' ? 'bg-light-primary' : salesStage === '02 - Identified' ? 'bg-light-dark' : salesStage === '03 - Committee' ? 'bg-light-secondary' : salesStage === "07 - External Revision" ? 'bg-light-danger' : 'bg-light-info'}`} style={{minHeight: '75%', minWidth: '75%', padding: "0", lineHeight: '3', fontSize: '14px'}}>{salesStage}</label>
                );
            },
        },
        {
            field: "quoteId",
            headerName: "View PDF",
            type: "boolean",
            flex: 1,
            valueGetter: (value) => Boolean(value),
            valueFormatter: (value) => value ? "Linked" : "Missing",
            align: "center",
            headerAlign: "center",
            renderCell: ({ row: { quoteId, quoteNumber } }) => {
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {quoteId ? (
                            <IconButton aria-label="View quote PDF" onClick={() => handleOpen(quoteId, quoteNumber)}>
                                <CheckCircle color="success" />
                            </IconButton>
                        ) : (
                            <Cancel color="error" />
                        )}
                    </Box>
                );
            }
        },
            {
                field: "updatedAt",
                headerName: "Last Updated",
                renderCell: ({ row: { updatedAt } }) => updatedAt ? updatedAt.split("T")[0] : "",
                flex: 1
            }        
    ];
};

export default OpportunitiesColumns
